/**
 * ApiClient - Facade Pattern
 * Central access point for all API services
 * Provides a clean, organized interface to the backend API
 */

import { HttpClient } from './HttpClient';
import { ChatApiService } from './ChatApiService';
import { DocumentApiService } from './DocumentApiService';
import { UserApiService } from './UserApiService';
import { GroupApiService } from './GroupApiService';
import type { AuthProvider } from '../auth/AuthProvider';
import { LocalStorageAuthProvider } from '../auth/AuthProvider';

/**
 * API Client configuration
 */
export interface ApiClientConfig {
  baseURL: string;
  authProvider?: AuthProvider;
}

/**
 * Main API Client using Facade pattern
 * Organizes all API endpoints into domain-specific services
 */
export class ApiClient {
  private readonly httpClient: HttpClient;

  // Domain-specific API services
  public readonly chat: ChatApiService;
  public readonly documents: DocumentApiService;
  public readonly users: UserApiService;
  public readonly groups: GroupApiService;

  constructor(config: ApiClientConfig) {
    // Use provided auth provider or default to localStorage
    const authProvider = config.authProvider || new LocalStorageAuthProvider();

    // Create HTTP client
    this.httpClient = new HttpClient(config.baseURL, authProvider);

    // Initialize all domain services
    this.chat = new ChatApiService(this.httpClient);
    this.documents = new DocumentApiService(this.httpClient);
    this.users = new UserApiService(this.httpClient);
    this.groups = new GroupApiService(this.httpClient);
  }

  /**
   * Get the underlying HTTP client for advanced usage
   * Use sparingly - prefer using domain services
   */
  public getHttpClient(): HttpClient {
    return this.httpClient;
  }
}

/**
 * Create a singleton API client instance
 * This ensures all parts of the app use the same client
 */
let apiClientInstance: ApiClient | null = null;

/**
 * Initialize the global API client
 * Should be called once at app startup
 */
export function initializeApiClient(config: ApiClientConfig): ApiClient {
  apiClientInstance = new ApiClient(config);
  return apiClientInstance;
}

/**
 * Get the global API client instance
 * Throws if not initialized
 */
export function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    throw new Error(
      'ApiClient not initialized. Call initializeApiClient() first in your app entry point.'
    );
  }
  return apiClientInstance;
}

/**
 * Reset the API client (useful for testing)
 */
export function resetApiClient(): void {
  apiClientInstance = null;
}
